import React from "react";
import { useTranslation } from "react-i18next";
import "./TelealarmeISSEquipment.css";

/**
 * Section 03 — Équipements réels de Téléalarme ISS.
 * Image d'origine du site : /public/images/Tele_part.png.
 * Les rôles présentés sont indicatifs et conditionnés à la configuration.
 * Aucun numéro de modèle, agrément ou certification non vérifié n'est ajouté.
 */
const COPY = {
  fr: {
    eyebrow: "LE PRODUIT, PAS UNE PROMESSE ABSTRAITE",
    title: "Du bouton d’alarme à la communication avec l’assistance.",
    intro:
      "Des composants identifiables, présentés par leur rôle dans la chaîne d’appel.",
    photoAlt:
      "Visuel des équipements Téléalarme ISS, sous réserve des modèles et versions disponibles.",
    photoNote: "Visuel produit · modèles et versions à confirmer",
    roles: [
      {
        num: "01",
        title: "Interface d’appel en cabine",
        text: "Point de déclenchement de l’alarme pour l’usager."
      },
      {
        num: "02",
        title: "Équipement de téléalarme",
        text: "Gestion de la communication d’urgence selon l’architecture configurée."
      },
      {
        num: "03",
        title: "Communication réseau 4G/IP",
        text: "Transmission vers le service d’assistance selon le matériel et la couverture réseau."
      }
    ],
    button: "Étudier mon installation",
    conditions:
      "Composition et compatibilité à confirmer selon l’ascenseur, les équipements et les conditions d’installation."
  },
  en: {
    eyebrow: "REAL EQUIPMENT, NOT AN ABSTRACT PROMISE",
    title: "From the alarm button to the assistance service.",
    intro:
      "Identifiable components, presented according to their role in the emergency call chain.",
    photoAlt:
      "Visual showing Téléalarme ISS equipment, subject to the models and versions available.",
    photoNote: "Product visual · models and versions to be confirmed",
    roles: [
      {
        num: "01",
        title: "In-car alarm interface",
        text: "The passenger's point of access to trigger an alarm."
      },
      {
        num: "02",
        title: "Emergency call equipment",
        text: "Manages emergency communication according to the chosen configuration."
      },
      {
        num: "03",
        title: "4G/IP network communication",
        text: "Connection to the assistance service, subject to equipment and network coverage."
      }
    ],
    button: "Discuss my installation",
    conditions:
      "Equipment configuration and compatibility are subject to the lift, installed devices and site conditions."
  }
};

export default function TelealarmeISSEquipment() {
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const content = COPY[lang] || COPY.fr;

  return (
    <section
      className="iss-equipment"
      id="iss-equipment-section"
      aria-labelledby="iss-equipment-title"
    >
      <div className="iss-equipment__container">
        <header className="iss-equipment__header">
          <p className="iss-equipment__eyebrow">{content.eyebrow}</p>
          <h2 className="iss-equipment__title" id="iss-equipment-title">
            {content.title}
          </h2>
          <p className="iss-equipment__lead">{content.intro}</p>
        </header>

        <div className="iss-equipment__grid">
          <figure className="iss-equipment__visual">
            <img
              className="iss-equipment__image"
              src="/images/Tele_part.png"
              alt={content.photoAlt}
              width="900"
              height="550"
              loading="lazy"
              decoding="async"
            />
            <figcaption className="iss-equipment__image-caption">
              {content.photoNote}
            </figcaption>
          </figure>

          <div className="iss-equipment__explanation">
            <ol className="iss-equipment__roles">
              {content.roles.map((role) => (
                <li className="iss-equipment__role" key={role.num}>
                  <span className="iss-equipment__role-number" aria-hidden="true">
                    {role.num}
                  </span>
                  <div className="iss-equipment__role-content">
                    <h3 className="iss-equipment__role-title">{role.title}</h3>
                    <p className="iss-equipment__role-text">{role.text}</p>
                  </div>
                </li>
              ))}
            </ol>

            <a className="iss-equipment__cta" href="/contact">
              <span>{content.button}</span>
              <svg aria-hidden="true" viewBox="0 0 24 24" width="17" height="17" fill="none">
                <path d="M5 19 19 5M8 5h11v11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>
        </div>

        <p className="iss-equipment__conditions">{content.conditions}</p>
      </div>
    </section>
  );
}
